import React from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card';
import { Checkbox } from '@swyftx/aviary/atoms/Checkbox';
import { Label } from '@swyftx/aviary/atoms/Label';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric, Utility } from '@swyftx/aviary/atoms/Typography';
import { Edit } from '@swyftx/aviary/icons/outlined';

import { Big } from '@shared/safe-big';

import { INTEREST } from '@utils/calculator';

import { Controller, useForm } from 'react-hook-form';

import { LoansStepContainer } from './LoansStepContainer';
import { useLoanApplicationSelector, useLoanApplicationService } from '../../xstate/LoanApplication.context';
import {
  EmploymentStatus,
  EmploymentStatusLabels,
  RelationshipStatus,
  RelationshipStatusLabels,
} from '../../xstate/types/LoanApplication.types';

const ReviewLoanRequestStep: React.FC = () => {
  const loanApplicationService = useLoanApplicationService();
  const applicationData = useLoanApplicationSelector((state) => state.context.applicationData);

  const {
    control,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
  });

  return (
    <LoansStepContainer customTitle='Review your application' submitDisabled={!isValid}>
      <main className='flex flex-col gap-32'>
        <Body>
          Review your loan application before submitting. Your application can&apos;t be changed after submission.
        </Body>

        <div className='flex flex-col gap-8'>
          <div className='flex justify-between'>
            <Body size='small' weight='emphasis'>
              Loan summary
            </Body>
            <Button
              leadingIcon={<Edit />}
              variant='filled'
              size='sm'
              onClick={() => loanApplicationService.send({ type: 'EDIT_LOAN_SUMMARY' })}
            >
              Edit
            </Button>
          </div>
          <Card>
            <div className='flex flex-col gap-8 p-24'>
              <div className='flex flex-row justify-between'>
                <Body size='small'>Loan type</Body>
                <Body size='small' weight='bold'>
                  {applicationData?.repaymentType === 'LINE_OF_CREDIT'
                    ? 'Line of Credit'
                    : applicationData?.repaymentType === 'FIXED_TERM'
                    ? 'Fixed Term'
                    : 'Not specified'}
                </Body>
              </div>
              <div className='flex flex-row justify-between'>
                <Body size='small'>Loan term</Body>
                <Body size='small' weight='bold'>
                  {applicationData?.termValue
                    ? `${applicationData.termValue} ${applicationData.termUnits || ''}`
                    : 'Up to 12 months'}
                </Body>
              </div>

              <div className='flex flex-row justify-between'>
                <Body size='small'>Interest rate (p.a.)</Body>
                <Body size='small' weight='bold'>
                  {Big(INTEREST).times(100).toString()}% (Fixed)
                </Body>
              </div>
              <div className='flex flex-row justify-between'>
                <Body size='small'>Loan amount*</Body>
                <Body size='small' weight='bold'>
                  ${applicationData?.borrowPrincipalAmount?.toString?.() || 'Not specified'}
                </Body>
              </div>

              <div className='flex flex-row justify-between'>
                <Body size='small'>Loan-to-value ratio (LVR)</Body>
                <Numeric size='small' weight='bold'>
                  {/* Placeholder, as LVR calculation may depend on other fields */}
                  Not specified
                </Numeric>
              </div>

              <div className='flex flex-row justify-between'>
                <Body size='small'>Origination fee (2%)</Body>
                <Numeric size='small' weight='bold'>
                  {/* Placeholder, as fee calculation may depend on other fields */}
                  Not specified
                </Numeric>
              </div>

              <div className='flex flex-row justify-between'>
                <Body size='small'>Total interest**</Body>
                <Numeric size='small' weight='bold'>
                  {/* Placeholder, as total interest calculation may depend on other fields */}
                  Not specified
                </Numeric>
              </div>
            </div>
          </Card>

          <Utility variant='caption'>
            *This is a projected amount, not the guaranteed amount that you will receive (Receiving funds is subject to
            approval). **This calculation is based the amount outstanding remaining the same for 12 months. Your
            individual situation may vary from the provided example.
          </Utility>
        </div>

        <div className='flex flex-col gap-8'>
          <div className='flex justify-between'>
            <Body size='small' weight='emphasis'>
              Information about you
            </Body>
            <Button
              leadingIcon={<Edit />}
              variant='filled'
              size='sm'
              onClick={() => loanApplicationService.send({ type: 'EDIT_ABOUT_YOU' })}
            >
              Edit
            </Button>
          </div>
          <Card>
            <div className='flex flex-col gap-8 p-24'>
              <div className='flex flex-row justify-between'>
                <Body size='small'>Employment status</Body>
                <Body size='small' weight='bold'>
                  {EmploymentStatusLabels[applicationData?.employmentStatus as EmploymentStatus] || 'Not specified'}
                </Body>
              </div>
              <div className='flex flex-row justify-between'>
                <Body size='small'>Relationship status</Body>
                <Body size='small' weight='bold'>
                  {RelationshipStatusLabels[applicationData?.relationshipStatus as RelationshipStatus] ||
                    'Not specified'}
                </Body>
              </div>
              <div className='flex flex-row justify-between'>
                <Body size='small'>Number of dependents</Body>
                <Numeric size='small' weight='bold'>
                  {applicationData.numDependents ?? 'Not specified'}
                </Numeric>
              </div>
            </div>
          </Card>
        </div>

        <div className='flex flex-col gap-8'>
          <div className='flex justify-between'>
            <Body size='small' weight='emphasis'>
              Information about your financials
            </Body>
            <Button
              leadingIcon={<Edit />}
              variant='filled'
              size='sm'
              onClick={() => loanApplicationService.send({ type: 'EDIT_FINANCIALS' })}
            >
              Edit
            </Button>
          </div>
          <Card>
            <div className='flex flex-col gap-8 p-24'>
              <div className='flex flex-row justify-between'>
                <Body size='small'>Current monthly income</Body>
                <Numeric size='small' weight='bold'>
                  ${applicationData?.monthlyIncome?.toString?.() || 'Not specified'}
                </Numeric>
              </div>
              {applicationData?.expectedMonthlyIncome != null && (
                <div className='flex flex-row justify-between'>
                  <Body size='small'>New monthly income</Body>
                  <Numeric size='small' weight='bold'>
                    ${applicationData?.expectedMonthlyIncome?.toString?.()}
                  </Numeric>
                </div>
              )}
              <div className='flex flex-row justify-between'>
                <Body size='small'>Current monthly expenses</Body>
                <Numeric size='small' weight='bold'>
                  ${applicationData?.monthlyExpenses?.toString?.() || 'Not specified'}
                </Numeric>
              </div>
              {applicationData?.expectedMonthlyExpenses != null && (
                <div className='flex flex-row justify-between'>
                  <Body size='small'>New monthly expenses</Body>
                  <Numeric size='small' weight='bold'>
                    ${applicationData?.expectedMonthlyExpenses?.toString?.()}
                  </Numeric>
                </div>
              )}
              <div className='flex flex-row justify-between'>
                <Body size='small'>Current credit limits</Body>
                <Numeric size='small' weight='bold'>
                  ${applicationData?.totalExistingCreditLimits?.toString?.() || 'Not specified'}
                </Numeric>
              </div>
              {applicationData?.expectedTotalExistingCreditLimits != null && (
                <div className='flex flex-row justify-between'>
                  <Body size='small'>New credit limits</Body>
                  <Numeric size='small' weight='bold'>
                    ${applicationData?.expectedTotalExistingCreditLimits?.toString?.()}
                  </Numeric>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className='flex flex-col gap-8'>
          <Body size='small' weight='emphasis'>
            Security required
          </Body>
          <Card>
            <div className='flex flex-col gap-8 p-24'>
              <div className='flex flex-row justify-between'>
                <Body size='small'>Asset</Body>
                <Body size='small' weight='bold'>
                  Bitcoin (BTC)
                </Body>
              </div>
              <div className='flex flex-row justify-between'>
                <Body size='small'>Estimated amount required</Body>
                <Body size='small' weight='bold'>
                  {`~${applicationData?.securityAmount?.toString?.()} BTC` || 'Not specified'}
                </Body>

                {/* TODO: security in AUD */}
                {/* <Body size='small' weight='bold'>
                  {`~${applicationData?.securityAmount?.toString?.()} AUD` || 'Not specified'}
                </Body> */}
              </div>
              <div className='flex flex-row justify-between'>
                <Body size='small'>When it&apos;s required</Body>
                <Body size='small' weight='bold'>
                  After you&apos;re approved
                </Body>
              </div>
            </div>
          </Card>
        </div>

        <FlexLayout direction='row' alignItems='start' spacing={8}>
          <Controller
            name='reviewDeclarationCheckbox'
            control={control}
            rules={{ required: 'You must declare to continue' }}
            render={({ field }) => (
              <Checkbox
                className='h-20 w-20 border-2 border-color-border-main'
                id='review-declaration-checkbox'
                checked={Boolean(field.value)}
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                }}
              />
            )}
          />
          <Label htmlFor='review-declaration-checkbox'>
            I declare that the information I have provided is true and accurate to the best of my knowledge. I
            understand that providing false or misleading information may have legal implications.
          </Label>
        </FlexLayout>
      </main>
    </LoansStepContainer>
  );
};

export { ReviewLoanRequestStep };
